import {
  Box,
  IconButton,
  Typography,
  Chip,
  alpha,
  Paper,
  useTheme,
} from '@mui/material';
import { genericFormProps } from '../signup.types';
import { verifyFileSize } from '../helper';
import { toast } from 'react-toastify';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useCallback, useState } from 'react';
import DeleteIcon from '../../../svg/DeleteIcon3';
import FileIcon2 from '../../../svg/FileIcon2';
import GoogleDriveIcon from '../../../svg/GoogleDriveIcon';
import PdfIcon from '../../../svg/PdfIcon';
import PdfOffIcon from '../../../svg/PdfOffIcon';
import TXTIcon from '../../../svg/TXTIcon';
import DocxIcon from '../../../svg/DoucmentIcon';
import PptIcon from '../../../svg/PptIcon';

const DocumentsForm: React.FC<genericFormProps> = (props) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    formSubmitting,
    isValid,
    dirty,
    showSteps,
  } = props;

  const theme = useTheme();
  const [isDragging, setIsDragging] = useState(false);

  const handleFileValidation = useCallback(
    (files: FileList) => {
      let newDocuments = [...values.userDocument];
      for (let i = 0; i < files?.length; i++) {
        const fileSize = Math.round(files[i]?.size / 1024);
        if (!verifyFileSize(fileSize, 1024000)) {
          continue;
        }
        const allowedTypes = ['txt', 'docx', 'pdf', 'pptx'];
        if (!allowedTypes.includes(files[i]?.name.split('.').pop())) {
          toast.error(
            'The file type selected is not supported. The only supported file types are: txt, docx, pdf, and pptx'
          );
          continue;
        }
        const allFiles = values?.userDocument?.concat(
          values?.existingUserDocument || []
        );
        const fileExistsIndex = allFiles?.findIndex(
          (fileItem: any) => fileItem?.name.trim() === files[i]?.name?.trim()
        );
        if (fileExistsIndex !== -1) {
          toast.error(`The file name "${files[i]?.name}" already exists`);
          continue;
        }
        newDocuments.push(files[i]);
      }
      if (newDocuments.length !== values.userDocument.length) {
        setFieldValue('userDocument', newDocuments);
      }
    },
    [values.userDocument, values.existingUserDocument, setFieldValue]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        handleFileValidation(files);
      }
    },
    [handleFileValidation]
  );

  const getFileIcon = useCallback(
    (fileName: string, source?: string, isIndexed?: boolean | null) => {
      if (source === 'google_drive') {
        return <GoogleDriveIcon width={24} height={24} />;
      }

      const ext = fileName?.split('.').pop()?.toLowerCase();

      if (ext === 'pdf') {
        if (isIndexed === false) {
          return <PdfOffIcon width={24} height={24} />;
        }
        return <PdfIcon width={24} height={24} />;
      }

      if (ext === 'txt') {
        return <TXTIcon width={24} height={24} />;
      }

      if (ext === 'docx' || ext === 'doc') {
        return <DocxIcon width={24} height={24} />;
      }

      if (ext === 'pptx' || ext === 'ppt') {
        return <PptIcon width={24} height={24} />;
      }

      return (
        <FileIcon2 width={24} height={24} fill={theme.palette.text.secondary} />
      );
    },
    [theme]
  );

  const renderFileListItem = useCallback(
    (document: any, index: number, onDeleteHandler: () => void) => {
      const isIndexed = document?.hasOwnProperty('is_indexed')
        ? document.is_indexed
        : null;
      const isNewFile = !document?.hasOwnProperty('is_indexed');

      return (
        <Box
          key={`${document.name}-${index}`}
          display='flex'
          flexDirection='column'
          borderBottom={1}
          borderColor='divider'
          px={2.5}
          py={1.5}
        >
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {getFileIcon(document?.name, document?.source, isIndexed)}

              <Typography
                variant='body2'
                sx={{
                  wordBreak: 'break-word',
                  color: 'text.primary',
                }}
              >
                {document && typeof document === 'object' && document?.name
                  ? document.name
                  : document || ''}
              </Typography>
            </Box>

            <IconButton
              id={`button-delete-${document.name}-${index}`}
              size='small'
              sx={{
                width: '30px',
                height: '30px',
              }}
              onClick={onDeleteHandler}
            >
              <DeleteIcon stroke={theme.palette['nav'].iconactive} />
            </IconButton>
          </Box>
        </Box>
      );
    },
    [theme, getFileIcon]
  );

  const hasDocuments =
    !!(values?.existingUserDocument || []).length ||
    !!(values?.userDocument || []).length;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      {/* Page Title */}
      <Box>
        <Typography
          variant='h5'
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 0.5,
          }}
        >
          Upload Documents
        </Typography>
        <Typography
          variant='body2'
          sx={{
            color: 'text.secondary',
          }}
        >
          Share your written content to help train your AI persona
        </Typography>
        <Typography
          sx={{
            color: 'primary.main',
            fontSize: 16,
            fontFamily: '"Merriweather Sans", sans-serif',
            fontWeight: 600,
            lineHeight: '20.8px',
            letterSpacing: '0.15px',
            mb: 2,
            mt: 2,
          }}
        >
          *One document or web link is required
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 4,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background['container-2'],
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'start',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <UploadFileIcon sx={{ color: 'text.primary' }} />
            <Typography variant='h7'>Add Documents</Typography>
          </Box>
          <Typography variant='body2'>
            Upload articles, blogs, scripts, or any other content you've created
            to give your AI persona a deeper understanding of your unique voice
            and expertise.
          </Typography>
        </Box>

        {/* Large Centered Upload Area with Dashed Border */}
        <Box
          component='label'
          htmlFor='file-input-userDocument'
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            display: 'block',
            p: 4,
            borderRadius: 3,
            border: '2px dashed',
            borderColor: isDragging
              ? theme.palette.primary.main
              : theme.palette.divider,
            bgcolor: isDragging
              ? alpha(theme.palette.primary.main, 0.1)
              : theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.4)
                : alpha(theme.palette.primary.main, 0.02),
            textAlign: 'center',
            transition: 'all 0.3s ease-in-out',
            cursor: 'pointer',
            '&:hover': {
              borderColor: theme.palette.primary.main,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
            },
          }}
        >
          <input
            type='file'
            hidden
            multiple
            id='file-input-userDocument'
            name='userDocument'
            onChange={(event) => {
              const files = event?.target?.files;
              if (files && files.length > 0) {
                handleFileValidation(files);
              }
            }}
          />

          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <UploadFileIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          </Box>

          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 4,
              py: 1.5,
              borderRadius: 2,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              fontWeight: 600,
              boxShadow: theme.shadows[4],
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: theme.shadows[8],
                transform: 'translateY(-2px)',
              },
            }}
          >
            <UploadFileIcon sx={{ fontSize: 20 }} />
            Choose Documents
          </Box>

          <Typography
            variant='body2'
            display='block'
            mt={1.5}
            sx={{
              color: theme.palette.text.secondary,
            }}
          >
            or drag and drop files here
          </Typography>

          <Typography
            variant='caption'
            display='block'
            mt={1}
            sx={{
              color: theme.palette.text.secondary,
            }}
          >
            Supported formats: TXT, DOCX, PDF, PPTX • Max size: 1GB per file
          </Typography>
        </Box>

        {/* Pro Tip Info Box */}
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.info.main, 0.08),
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography
            variant='body2'
            sx={{
              color: 'info.main',
              fontWeight: 500,
            }}
          >
            💡 <strong>Pro tip:</strong> Upload all your files at once for
            faster processing
          </Typography>
        </Box>
      </Paper>

      {/* Documents List Section */}
      {hasDocuments && (
        <Box mt={1}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: theme.palette.background['container-2'],
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <FileIcon2
                fill={theme.palette.primary.contrastText}
                color={theme.palette.secondary.contrastText}
              />
              <Typography variant='h7'>My Documents</Typography>
            </Box>
            <Chip
              label={`${(values?.existingUserDocument || []).length + (values?.userDocument || []).length} files`}
              size='small'
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
              }}
            />
          </Paper>

          <Box>
            {(values?.existingUserDocument || []).map(
              (document: any, index: number) =>
                renderFileListItem(document, index, () => {
                  let existing = [...values.existingUserDocument];
                  existing?.splice(index, 1);
                  setFieldValue('existingUserDocument', existing);
                  let deleted = [...values.deletedUserDocument];
                  deleted?.push(document);
                  setFieldValue('deletedUserDocument', deleted);
                })
            )}
            {(values?.userDocument || []).map((document: any, index: number) =>
              renderFileListItem(document, index, () => {
                let docs = [...values.userDocument];
                docs?.splice(index, 1);
                setFieldValue('userDocument', docs || null);
              })
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DocumentsForm;
